/*
 * Trash Page Panel Template
 */
import type {
  PanelArea,
  PanelsBreakpointTemplate,
  PanelsTemplate,
  TrashPageArea,
} from '../types';
import {
  LARGE_NAV_DEFAULT_WIDTH,
  MEDIUM_NAV_MAX_WIDTH,
  MEDIUM_NAV_MIN_WIDTH,
  PROJECT_NAV_MIN_WIDTH,
} from './constants';
import TEXT from './generated/TEXT/trash';

TEXT.DEFINE({
  directoryNav: {
    accessibility: 'Toggle the Project Navigation',
    hide: 'Hide Project Navigation',
    show: 'Show Project Navigation',
  },
  directoryContent: {
    accessibility: 'Toggle the Deleted Assets view',
    hide: 'Hide the Deleted Assets view',
    show: 'Show the Deleted Assets view',
  },
});

/**
 * Base template (Mobile +)
 */
export function baseTemplate(
  __visible: Array<PanelArea>
): PanelsBreakpointTemplate {
  return {
    areas: [['directoryNav', 'directoryContent', 'viewer', 'details', 'ai']],
    resizers: {
      directoryNav: 'right',
      directoryContent: 'right',
      viewer: 'right',
    },
    columns: [
      /* Project Nav uses the PrimarySheetNav tunnel on smaller screens so it's always hidden. */
      {
        key: 'directoryNav',
        defaultSize: MEDIUM_NAV_MIN_WIDTH,
        isToggleable: false,
        showing: false,
      },

      /* Asset Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        panelToggle: {
          label: TEXT.directoryContent,
          variant: 'center',
          disabled: true,
        },
        showing: true,
      },

      /* Viewer Panel */
      {
        key: 'viewer',
        defaultSize: '0fr',
        isToggleable: false,
        showing: false,
      },

      /* Details Panel */
      {
        key: 'details',
        defaultSize: '0fr',
        isToggleable: false,
        showing: false,
      },

      /* AI Panel (hidden by default) */
      {
        key: 'ai',
        defaultMin: 0,
        defaultSize: 0,
        isToggleable: false,
        showing: false,
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
}

/**
 * Medium template
 */
export const mediumTemplate = (
  visible: Array<PanelArea>
): PanelsBreakpointTemplate => {
  return {
    areas: [['directoryNav', 'directoryContent', 'viewer', 'details', 'ai']],
    resizers: {
      directoryNav: 'right',
      directoryContent: 'right',
      viewer: 'right',
    },
    columns: [
      /* Project Nav */
      {
        key: 'directoryNav',
        defaultMin: PROJECT_NAV_MIN_WIDTH,
        defaultSize: PROJECT_NAV_MIN_WIDTH,
        defaultMax: MEDIUM_NAV_MAX_WIDTH,
        isToggleable: true,
        panelToggle: {
          label: TEXT.directoryNav,
          variant: 'left',
        },
        showing: visible.includes('directoryNav'),
      },

      /* Asset Grid */
      {
        key: 'directoryContent',
        defaultMin: 300,
        defaultSize: '1fr',
        isToggleable: false,
        panelToggle: {
          label: TEXT.directoryContent,
          variant: 'center',
          disabled: true,
        },
        showing: true,
      },

      /* Viewer Panel */
      {
        key: 'viewer',
        defaultSize: '0fr',
        isToggleable: false,
        showing: false,
      },

      /* Details Panel */
      {
        key: 'details',
        defaultSize: '0fr',
        isToggleable: false,
        panelToggle: {
          label: TEXT.directoryContent,
          variant: 'right',
          disabled: true,
        },
        showing: false,
      },

      /* AI Panel (hidden by default) */
      {
        key: 'ai',
        defaultMin: 0,
        defaultSize: 0,
        isToggleable: false,
        showing: false,
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: false,
      },
    ],
  };
};

/**
 * Large template
 */
export const largeTemplate = (
  visible: Array<PanelArea>
): PanelsBreakpointTemplate => {
  return {
    areas: [['directoryNav', 'directoryContent', 'viewer', 'details', 'ai']],
    resizers: {
      directoryNav: 'right',
      directoryContent: 'right',
      viewer: 'right',
    },
    columns: [
      /* Project Nav */
      {
        key: 'directoryNav',
        defaultMin: PROJECT_NAV_MIN_WIDTH,
        defaultSize: LARGE_NAV_DEFAULT_WIDTH,
        defaultMax: MEDIUM_NAV_MAX_WIDTH,
        isToggleable: true,
        panelToggle: {
          label: TEXT.directoryNav,
          variant: 'left',
        },
        showing: visible.includes('directoryNav'),
      },

      /* Asset Grid */
      {
        key: 'directoryContent',
        defaultMin: 208,
        defaultSize: '1fr',
        panelToggle: {
          label: TEXT.directoryContent,
          variant: 'center',
          disabled: true,
        },
        isToggleable: false,
        showing: true,
      },

      /* Viewer Panel */
      {
        key: 'viewer',
        defaultSize: '0fr',
        isToggleable: false,
        showing: false,
      },

      /* Details Panel */
      {
        key: 'details',
        defaultSize: '0fr',
        isToggleable: false,
        panelToggle: {
          label: TEXT.directoryContent,
          variant: 'right',
          disabled: true,
        },
        showing: false,
      },

      /* AI Panel (hidden by default) */
      {
        key: 'ai',
        defaultMin: 0,
        defaultSize: 0,
        isToggleable: false,
        showing: false,
      },
    ],
    rows: [
      {
        key: 'defaultRow',
        defaultMin: 200,
        defaultSize: '1fr',
        isToggleable: true,
      },
    ],
  };
};

export function getPanelState(visible: Array<PanelArea>): PanelsTemplate {
  return {
    base: baseTemplate(visible),
    tablet: mediumTemplate(visible),
    desktop: largeTemplate(visible),
    visible,
  };
}

export const getDefaultVisiblePanels = (): Array<TrashPageArea> => {
  return ['directoryNav', 'directoryContent'];
};

// Because this page encapsulates three "pages", each has a different layout default
export const getDefaultPanelState = (): PanelsTemplate => {
  return getPanelState(getDefaultVisiblePanels());
};
